import React, { useMemo, useState } from 'react';
import { Alert } from 'react-bootstrap';

import { useChat } from '../../../Hooks/UseChat';
import { TChat } from '../../../Types/Chat';

import ChatList from '../ChatList/ChatList';
import ChatProjectsTree from '../ChatProjectsTree/ChatProjectsTree';
import UserAvatar from '../../UserAvatar/UserAvatar';
import XChatListItem from '../ChatListItem/ChatListItem';

import './ChatSidebar.scss';

interface Props {
  onSelectChat?: () => void;
}

type SidebarSection = 'general' | 'project' | 'teams' | 'private';
type SidebarGroup = 'pinned' | 'active' | 'projects';

const sidebarTabs: Array<{
  id: SidebarSection;
  label: string;
  icon: string;
}> = [
  { id: 'project', label: 'Proyecto', icon: 'far fa-fw fa-building' },
  { id: 'general', label: 'General', icon: 'fas fa-fw fa-hashtag' },
  { id: 'teams', label: 'Equipos', icon: 'fas fa-fw fa-users' },
  { id: 'private', label: 'Privados', icon: 'far fa-fw fa-user-circle' },
];

const ChatSidebar: React.FC<Props> = ({ onSelectChat }: Props) => {
  const { connected, currentChat, lastChats, joinChat, projectsTree } = useChat();

  const [ section, setSection ] = useState<SidebarSection>('general');
  const [ search, setSearch ] = useState('');
  const [ selectedTeamId, setSelectedTeamId ] = useState('');
  const [ openGroups, setOpenGroups ] = useState<Record<SidebarGroup, boolean>>({
    pinned: true,
    active: true,
    projects: true,
  });

  const projectCount = projectsTree.length;
  const teamCount = projectsTree.reduce(
    (total: number, project: any) => total + (project.teams?.length || 0),
    0
  );
  const privateCount = lastChats.filter((chat: TChat) => chat.type === 'private').length;
  const tabCounts: Record<SidebarSection, number> = {
    project: projectCount,
    general: lastChats.length,
    teams: teamCount,
    private: privateCount,
  };

  const searchValue = search.trim().toLowerCase();

  const filteredChats = useMemo(() => {
    const chats = section === 'private'
      ? lastChats.filter((chat: TChat) => chat.type === 'private')
      : lastChats;

    if (!searchValue) return chats;

    return chats.filter((chat: TChat) => {
      const title = chat.title
        || chat.project?.name
        || chat.team?.name
        || `${chat.user?.name || ''} ${chat.user?.surname || ''}`;
      const lastMessage = chat.lastMessage?.content || '';

      return `${title} ${lastMessage}`.toLowerCase().includes(searchValue);
    });
  }, [ lastChats, searchValue, section ]);

  const teams = useMemo(() => {
    const result: any[] = [];

    projectsTree.forEach((project: any) => {
      project.teams?.forEach((team: any) => {
        result.push({
          ...team,
          projectName: project.name,
          projectId: project._id,
        });
      });
    });

    if (!searchValue) return result;

    return result.filter((team: any) => (
      `${team.name || ''} ${team.projectName || ''}`.toLowerCase().includes(searchValue)
    ));
  }, [ projectsTree, searchValue ]);

  const selectedTeam = useMemo(() => {
    if (!teams.length) return null;

    const activeTeamId = currentChat?.type === 'team'
      ? currentChat?.team?._id
      : selectedTeamId;

    return teams.find((team: any) => team._id === activeTeamId) || teams[0];
  }, [ currentChat, selectedTeamId, teams ]);

  const pinnedChats = useMemo(() => {
    const chatsWithPriority = filteredChats.filter(
      (chat: TChat) => chat.hasNewMessages || (chat as any).pinned
    );

    return (chatsWithPriority.length ? chatsWithPriority : filteredChats.slice(0, 3)).slice(0, 3);
  }, [ filteredChats ]);

  const activeChats = useMemo(() => {
    const pinnedIds = new Set(pinnedChats.map((chat: TChat) => chat._id));

    return filteredChats.filter((chat: TChat) => !pinnedIds.has(chat._id));
  }, [ filteredChats, pinnedChats ]);

  const handleSelectChat = (chatId: string) => {
    joinChat([ '', chatId ]);

    if (onSelectChat) onSelectChat();
  };

  const handleSelectTeam = (teamId: string) => {
    setSelectedTeamId(teamId);
    joinChat([ 'team', teamId ]);

    if (onSelectChat) onSelectChat();
  };

  const handleTabKeyDown = (
    event: React.KeyboardEvent<HTMLButtonElement>,
    index: number
  ) => {
    if (![ 'ArrowLeft', 'ArrowRight', 'Home', 'End' ].includes(event.key)) return;

    event.preventDefault();

    const lastIndex = sidebarTabs.length - 1;
    let nextIndex = index;

    if (event.key === 'ArrowRight') nextIndex = index === lastIndex ? 0 : index + 1;
    if (event.key === 'ArrowLeft') nextIndex = index === 0 ? lastIndex : index - 1;
    if (event.key === 'Home') nextIndex = 0;
    if (event.key === 'End') nextIndex = lastIndex;

    setSection(sidebarTabs[nextIndex].id);
  };

  const toggleGroup = (group: SidebarGroup) => {
    setOpenGroups({
      ...openGroups,
      [group]: !openGroups[group],
    });
  };

  const handleOpenProjects = () => {
    setSection('project');
    setOpenGroups({
      ...openGroups,
      projects: true,
    });
  };

  const renderGroup = (
    group: SidebarGroup,
    title: string,
    count: number,
    icon: string,
    children: React.ReactNode
  ) => (
    <section className={`chat-sidebar-group ${openGroups[group] ? 'open' : ''}`}>
      <button
        className="chat-sidebar-group-header"
        type="button"
        onClick={() => toggleGroup(group)}
      >
        <span>
          <i className={icon}></i>
          {title}
        </span>
        <em>{count}</em>
        <i className="fas fa-chevron-down"></i>
      </button>
      {openGroups[group] && (
        <div className="chat-sidebar-group-body">
          {children}
        </div>
      )}
    </section>
  );

  return (
    <div className="x-chat-sidebar">
      <div className="chat-workspace-switcher">
        <div className="chat-workspace-nav" role="tablist" aria-label="Secciones de chat">
          {sidebarTabs.map((tab, index) => (
            <button
              key={tab.id}
              aria-label={`${tab.label}: ${tabCounts[tab.id]}`}
              aria-controls={`chat-sidebar-panel-${tab.id}`}
              aria-selected={section === tab.id}
              className={`chat-workspace-nav-item ${section === tab.id ? 'active' : ''}`}
              id={`chat-sidebar-tab-${tab.id}`}
              role="tab"
              tabIndex={section === tab.id ? 0 : -1}
              title={tab.label}
              type="button"
              onClick={() => setSection(tab.id)}
              onKeyDown={(event) => handleTabKeyDown(event, index)}
            >
              <i className={tab.icon}></i>
              <span>{tab.label}</span>
              <strong>{tabCounts[tab.id]}</strong>
            </button>
          ))}
        </div>

        <button
          aria-label="Explorar proyectos"
          className="chat-workspace-create-team"
          title="Explorar proyectos"
          type="button"
          onClick={handleOpenProjects}
        >
          <i className="fas fa-plus"></i>
        </button>
      </div>

      <div className="chat-sidebar-search">
        <i className="fas fa-fw fa-search"></i>
        <input
          type="text"
          placeholder="Buscar"
          aria-label="Buscar conversaciones"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
      </div>

      <div
        aria-labelledby={`chat-sidebar-tab-${section}`}
        className="x-chat-sidebar-body custom-scrollbar"
        id={`chat-sidebar-panel-${section}`}
        role="tabpanel"
      >
        {!connected && (
          <Alert variant="warning">You are not connected to the chat server.</Alert>
        )}

        {section === 'general' && (
          <div className="chat-sidebar-groups">
            {renderGroup(
              'pinned',
              'Chats anclados',
              pinnedChats.length,
              'fas fa-thumbtack',
              <>
                {!pinnedChats.length && (
                  <div className="chat-sidebar-empty compact">
                    No hay chats anclados todavía.
                  </div>
                )}
                {pinnedChats.map((chat: TChat) => (
                  <XChatListItem
                    key={chat._id}
                    chat={chat}
                    onClick={() => handleSelectChat(chat._id)}
                  />
                ))}
              </>
            )}

            {renderGroup(
              'active',
              'Todos los chats activos',
              activeChats.length,
              'fas fa-comment-alt',
              <>
                {!activeChats.length && (
                  <div className="chat-sidebar-empty compact">
                    No encontramos chats activos con ese criterio.
                  </div>
                )}
                {activeChats.map((chat: TChat) => (
                  <XChatListItem
                    key={chat._id}
                    chat={chat}
                    onClick={() => handleSelectChat(chat._id)}
                  />
                ))}
              </>
            )}

            {renderGroup(
              'projects',
              'Todos los proyectos',
              projectCount,
              'far fa-building',
              <ChatProjectsTree
                embedded
                searchQuery={search}
                onSelectItem={onSelectChat}
              />
            )}
          </div>
        )}

        {section === 'private' && (
          <ChatList
            chats={filteredChats}
            onSelectChat={(chat: TChat) => handleSelectChat(chat._id)}
            emptyMessage="Aún no hay chats privados. Abre un proyecto o equipo para iniciar una conversación."
          />
        )}

        {section === 'project' && (
          <div className="chat-sidebar-groups">
            {renderGroup(
              'projects',
              'Todos los proyectos',
              projectCount,
              'far fa-building',
              <ChatProjectsTree
                embedded
                searchQuery={search}
                onSelectItem={onSelectChat}
              />
            )}
          </div>
        )}

        {section === 'teams' && (
          <div className="chat-teams-manager">
            <div className="chat-teams-list-header">
              <strong>Team Manager</strong>
              <span>{teams.length} equipos</span>
            </div>

            {!teams.length && (
              <div className="chat-sidebar-empty">
                No encontramos equipos con ese criterio.
              </div>
            )}

            <div className="chat-teams-list">
              {teams.map((team: any) => (
                <button
                  key={team._id}
                  className={`chat-team-item ${selectedTeam?._id === team._id ? 'active' : ''}`}
                  type="button"
                  onClick={() => handleSelectTeam(team._id)}
                >
                  <span>
                    <strong>{team.name}</strong>
                    <small>{team.projectName}</small>
                  </span>
                  <em>
                    <i className="fas fa-fw fa-users"></i>
                    {team.users?.length || 0}
                  </em>
                </button>
              ))}
            </div>

            {selectedTeam && (
              <div className="chat-team-detail">
                <div className="chat-team-detail-title">
                  <strong>{selectedTeam.name}</strong>
                  <span>Usuarios activos ({selectedTeam.users?.length || 0})</span>
                </div>
                <div className="chat-team-users">
                  {!selectedTeam.users?.length && (
                    <div className="chat-team-empty-users">
                      Este equipo aún no tiene usuarios.
                    </div>
                  )}
                  {selectedTeam.users?.map((user: any) => (
                    <div className="chat-team-user" key={user._id}>
                      <UserAvatar user={user} />
                      <span>
                        <strong>{user.name} {user.surname}</strong>
                        <small>{user.email || selectedTeam.name}</small>
                      </span>
                      <i className="fas fa-fw fa-ellipsis-v"></i>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ChatSidebar;
